"use client";

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { MindstringPrototype, Situation } from "@/generated/fastapi";

type StudioReassignStateContextValue = {
  reassignToSituation: Situation[];
  setReassignToSituation: Dispatch<SetStateAction<Situation[]>>;
  selectedThoughtConfirmationPrototypes: MindstringPrototype[];
  setSelectedThoughtConfirmationPrototypes: Dispatch<
    SetStateAction<MindstringPrototype[]>
  >;
  reassignAllThoughts: boolean;
  setReassignAllThoughts: Dispatch<SetStateAction<boolean>>;
};

const StudioReassignStateContext = createContext<
  StudioReassignStateContextValue | undefined
>(undefined);

export function StudioReassignStateProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [reassignToSituation, setReassignToSituation] = useState<Situation[]>(
    [],
  );
  const [
    selectedThoughtConfirmationPrototypes,
    setSelectedThoughtConfirmationPrototypes,
  ] = useState<MindstringPrototype[]>([]);
  const [reassignAllThoughts, setReassignAllThoughts] = useState(false);

  const value = useMemo(
    () => ({
      reassignToSituation,
      setReassignToSituation,
      selectedThoughtConfirmationPrototypes,
      setSelectedThoughtConfirmationPrototypes,
      reassignAllThoughts,
      setReassignAllThoughts,
    }),
    [
      reassignToSituation,
      selectedThoughtConfirmationPrototypes,
      reassignAllThoughts,
    ],
  );

  return (
    <StudioReassignStateContext.Provider value={value}>
      {children}
    </StudioReassignStateContext.Provider>
  );
}

export function useStudioReassignStateContext() {
  const context = useContext(StudioReassignStateContext);

  if (context === undefined) {
    throw new Error(
      "useStudioReassignStateContext must be used within a StudioReassignStateProvider",
    );
  }

  return context;
}
